// src/pages/users/operators/Operators.js
import React, { useEffect, useCallback, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Modal,
  Spinner,
  Alert,
  InputGroup,
  FormControl,
  Pagination,
  Dropdown,
  Tabs,
  Tab,
  Form,
} from "react-bootstrap";
import api from "../../../services/api";
import { Colors } from "../../../constants/Colors";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

/* ================================================================== */
/* CONSTANTS                                                          */
/* ================================================================== */

const MySwal = withReactContent(Swal);
const ITEMS_PER_PAGE = 10;
const COMMISSION_FAIL_THRESHOLD = 3;

const T = {
  radius: 14,
  radiusSm: 10,
  shadow: "0 1px 2px rgba(16,24,40,.04), 0 4px 16px rgba(16,24,40,.06)",
  shadowHover: "0 4px 8px rgba(16,24,40,.06), 0 12px 28px rgba(16,24,40,.10)",
  border: `1px solid ${Colors.BORDER_GRAY}`,
  font: "'Inter', 'Segoe UI', system-ui, sans-serif",
};

const SwalOnTop = Swal.mixin({
  customClass: { container: "swal-on-top" },
  didOpen: () => {
    const c = document.querySelector(".swal-on-top");
    if (c) c.style.zIndex = "20000";
  },
});

const FALLBACK_BASE_URL =
  process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, "") ||
  "http://localhost:8000";

/* ---------------- Document definitions ---------------- */
const DOCUMENT_SLOTS = [
  { key: "government_id_image", label: "Government ID", required: true },
  {
    key: "cpc_or_provisional_authority",
    label: "CPC / Provisional Authority",
    required: true,
  },
  { key: "insurance_image", label: "Vehicle Insurance", required: true },
  { key: "business_permit_image", label: "Business Permit", required: true },
  {
    key: "confirmation_certificate",
    label: "Confirmation Certificate",
    required: false,
  },
];

const REQUIRED_DOC_KEYS = DOCUMENT_SLOTS.filter((d) => d.required).map(
  (d) => d.key,
);

/* ---------------- Status metadata ---------------- */
const STATUS_META = {
  Active: { color: Colors.SUCCESS, bg: `${Colors.SUCCESS}1f`, label: "Active" },
  Inactive: { color: Colors.RED, bg: `${Colors.RED}1f`, label: "Inactive" },
  Pending: {
    color: Colors.WARNING,
    bg: `${Colors.WARNING}1f`,
    label: "Pending",
  },
  Rejected: {
    color: Colors.TEXT_LIGHT,
    bg: `${Colors.GRAY}26`,
    label: "Rejected",
  },
};

const STATUS_OPTIONS = [
  { value: "All", label: "All Status" },
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
  { value: "Pending", label: "Pending" },
  { value: "Rejected", label: "Rejected" },
];

/* ================================================================== */
/* HELPERS                                                            */
/* ================================================================== */

const initials = (f = "", l = "") =>
  `${(f[0] || "").toUpperCase()}${(l[0] || "").toUpperCase()}` || "?";

const getStatusMeta = (status) => {
  const key =
    (status || "").charAt(0).toUpperCase() +
    (status || "").slice(1).toLowerCase();
  return (
    STATUS_META[key] || {
      color: Colors.GRAY,
      bg: `${Colors.GRAY}26`,
      label: status || "—",
    }
  );
};

// 👇 UPDATED: Now supports full S3 URLs directly
const buildDocumentUrl = (pathOrUrl) => {
  if (!pathOrUrl) return null;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl; // S3 URL
  return `${FALLBACK_BASE_URL}/storage/${pathOrUrl.replace(/^\/+/, "")}`;
};

const hasCompleteRequiredDocuments = (op) =>
  REQUIRED_DOC_KEYS.every((k) => !!op?.[k]);

const getMissingRequiredDocs = (op) =>
  DOCUMENT_SLOTS.filter((d) => d.required && !op?.[d.key]).map((d) => d.label);

const formatDateTime = (d) => (d ? new Date(d).toLocaleString() : "N/A");

/* ---------------- Alert helpers ---------------- */
const showLoadingAlert = (title = "Loading...", text = "Please wait") =>
  SwalOnTop.fire({
    title,
    text,
    allowOutsideClick: false,
    didOpen: () => SwalOnTop.showLoading(),
  });

const showSuccessAlert = (title, text) =>
  SwalOnTop.fire({
    title,
    text,
    icon: "success",
    confirmButtonColor: Colors.PRIMARY,
    timer: 2000,
    timerProgressBar: true,
  });

const showErrorAlert = (title, text) =>
  SwalOnTop.fire({
    title,
    text,
    icon: "error",
    confirmButtonColor: Colors.PRIMARY,
  });

const showConfirmationAlert = (title, text, confirmButtonText = "Confirm") =>
  SwalOnTop.fire({
    title,
    text,
    icon: "question",
    showCancelButton: true,
    confirmButtonColor: Colors.PRIMARY,
    cancelButtonColor: "#6c757d",
    confirmButtonText,
    cancelButtonText: "Cancel",
  });

/* ================================================================== */
/* STYLES                                                             */
/* ================================================================== */

const Styles = () => (
  <style>{`
    .bk-page { font-family: ${T.font}; background: ${Colors.LIGHT_GRAY}; min-height: 100vh; padding: 24px 0 60px; }
    .bk-page .container-fluid { max-width: 1400px; }
    .bk-header { background: linear-gradient(135deg, ${Colors.GRADIENT_START} 0%, ${Colors.GRADIENT_END} 100%); color: #fff; border-radius: ${T.radius}px; padding: 26px 30px; margin-bottom: 20px; position: relative; overflow: hidden; box-shadow: 0 10px 30px rgba(14,107,248,.22); }
    .bk-header::after { content: ""; position: absolute; top: -60px; right: -60px; width: 220px; height: 220px; background: radial-gradient(circle, rgba(255,255,255,.18) 0%, transparent 70%); border-radius: 50%; }
    .bk-header h2 { font-weight: 700; letter-spacing: -.02em; margin: 0; font-size: 22px; }
    .bk-header p { opacity: .88; margin: 4px 0 0; font-size: 13.5px; }

    .bk-stat { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; padding: 16px 18px; display: flex; align-items: center; gap: 12px; transition: transform .18s ease, box-shadow .18s ease; height: 100%; cursor: default; }
    .bk-stat:hover { transform: translateY(-2px); box-shadow: ${T.shadowHover}; }
    .bk-stat-icon { width: 44px; height: 44px; border-radius: 11px; display: grid; place-items: center; font-size: 19px; flex-shrink: 0; }
    .bk-stat-val { font-size: 21px; font-weight: 700; line-height: 1; color: ${Colors.TEXT_DARK}; }
    .bk-stat-lbl { font-size: 11px; color: ${Colors.TEXT_LIGHT}; text-transform: uppercase; letter-spacing: .05em; margin-top: 4px; font-weight: 600; }

    .bk-card { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; box-shadow: ${T.shadow}; overflow: hidden; }
    .bk-card-header { padding: 16px 22px; border-bottom: ${T.border}; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; background: #fff; }
    .bk-card-header h5 { margin: 0; font-weight: 600; color: ${Colors.TEXT_DARK}; font-size: 15.5px; display: flex; align-items: center; gap: 8px; }
    .bk-card-header small { font-size: 12.5px; color: ${Colors.TEXT_LIGHT}; }

    .bk-toolbar { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; padding: 12px; box-shadow: ${T.shadow}; margin-bottom: 18px; }
    .bk-search { border: 1px solid transparent !important; background: ${Colors.LIGHT_GRAY} !important; border-radius: ${T.radiusSm} !important; padding: 9px 13px !important; font-size: 13.5px; transition: all .18s; height: auto !important; }
    .bk-search:focus { background: #fff !important; border-color: ${Colors.PRIMARY} !important; box-shadow: 0 0 0 3px ${Colors.PRIMARY}22 !important; }
    .bk-search-icon { background: ${Colors.LIGHT_GRAY} !important; border: 1px solid transparent !important; border-right: none !important; border-radius: ${T.radiusSm} 0 0 ${T.radiusSm} !important; color: ${Colors.GRAY}; }
    .bk-filter-btn { background: ${Colors.LIGHT_GRAY} !important; border: 1px solid transparent !important; color: ${Colors.TEXT_DARK} !important; border-radius: ${T.radiusSm} !important; padding: 9px 13px !important; font-size: 13.5px; font-weight: 500; text-align: left; display: flex; align-items: center; justify-content: space-between; }
    .bk-filter-btn:hover, .bk-filter-btn:focus { background: #fff !important; border-color: ${Colors.PRIMARY} !important; box-shadow: none !important; color: ${Colors.TEXT_DARK} !important; }
    .bk-filter-btn::after { margin-left: auto; }

    .bk-table { margin: 0; }
    .bk-table thead th { background: ${Colors.LIGHT_GRAY}; border: none; font-size: 11px; text-transform: uppercase; letter-spacing: .06em; color: ${Colors.TEXT_LIGHT}; font-weight: 700; padding: 13px 18px; white-space: nowrap; }
    .bk-table tbody td { padding: 15px 18px; border-color: ${Colors.BORDER_GRAY}; vertical-align: middle; font-size: 13.5px; color: ${Colors.TEXT_DARK}; }
    .bk-table tbody tr { transition: background .15s; }
    .bk-table tbody tr:hover { background: ${Colors.LIGHT_GRAY}55; }
    .bk-table tbody td.col-actions,
    .bk-table thead th.col-actions { width: 1%; white-space: nowrap; text-align: right; }

    .bk-avatar { width: 34px; height: 34px; border-radius: 10px; background: linear-gradient(135deg, ${Colors.GRADIENT_START}, ${Colors.GRADIENT_END}); color: #fff; font-weight: 600; font-size: 13px; display: grid; place-items: center; flex-shrink: 0; }
    .bk-id-chip { font-family: ui-monospace, "SF Mono", Menlo, monospace; font-size: 10.5px; font-weight: 600; background: ${Colors.LIGHT_GRAY}; color: ${Colors.TEXT_LIGHT}; padding: 2px 6px; border-radius: 5px; }

    .bk-pill { display: inline-flex; align-items: center; gap: 6px; padding: 4px 11px; border-radius: 999px; font-size: 11.5px; font-weight: 600; line-height: 1.4; }
    .bk-pill::before { content: ""; width: 6px; height: 6px; border-radius: 50%; background: currentColor; }

    .bk-empty { text-align: center; padding: 60px 20px; }
    .bk-empty-icon { width: 80px; height: 80px; margin: 0 auto 18px; border-radius: 50%; background: ${Colors.LIGHT_GRAY}; display: grid; place-items: center; font-size: 34px; color: ${Colors.GRAY}; }
    .bk-empty h5 { font-weight: 600; margin-bottom: 6px; }

    .bk-skel { background: linear-gradient(90deg, ${Colors.LIGHT_GRAY} 25%, #eee 50%, ${Colors.LIGHT_GRAY} 75%); background-size: 200% 100%; animation: bk-shimmer 1.4s infinite; border-radius: 6px; }
    @keyframes bk-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

    .bk-btn-primary { background: ${Colors.PRIMARY} !important; border: none !important; color: #fff !important; border-radius: ${T.radiusSm} !important; font-weight: 600; padding: 8px 16px; font-size: 13.5px; transition: all .18s; }
    .bk-btn-primary:hover { background: #e87a2e !important; transform: translateY(-1px); box-shadow: 0 6px 16px ${Colors.PRIMARY}44; }
    .bk-btn-primary:disabled { opacity: .5 !important; cursor: not-allowed !important; transform: none !important; box-shadow: none !important; }
    .bk-btn-ghost { background: transparent !important; border: 1px solid ${Colors.BORDER_GRAY} !important; color: ${Colors.TEXT_DARK} !important; border-radius: ${T.radiusSm} !important; font-weight: 500; padding: 7px 13px; font-size: 13px; transition: all .18s; }
    .bk-btn-ghost:hover { border-color: ${Colors.PRIMARY} !important; color: ${Colors.PRIMARY} !important; background: ${Colors.PRIMARY}0d !important; }
    .bk-icon-btn { width: 34px; height: 34px; padding: 0 !important; border: 1px solid ${Colors.BORDER_GRAY} !important; background: #fff !important; border-radius: 8px !important; display: grid !important; place-items: center; color: ${Colors.TEXT_DARK} !important; transition: all .18s; }
    .bk-icon-btn:hover { border-color: ${Colors.PRIMARY} !important; color: ${Colors.PRIMARY} !important; }
    .bk-icon-btn::after { display: none !important; }

    .bk-section { background: ${Colors.LIGHT_GRAY}; border-radius: ${T.radiusSm}; padding: 16px; margin-bottom: 14px; }
    .bk-section-title { font-size: 11.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: ${Colors.TEXT_LIGHT}; margin-bottom: 12px; display: flex; align-items: center; gap: 7px; }
    .bk-section-title i { color: ${Colors.PRIMARY}; font-size: 14px; }
    .bk-info-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px dashed ${Colors.BORDER_GRAY}; font-size: 13px; gap: 12px; }
    .bk-info-row:last-child { border-bottom: none; }
    .bk-info-label { color: ${Colors.TEXT_LIGHT}; flex-shrink: 0; }
    .bk-info-value { color: ${Colors.TEXT_DARK}; font-weight: 600; text-align: right; max-width: 65%; word-break: break-word; }

    .modal-content { border-radius: ${T.radius}px; border: none; box-shadow: 0 24px 60px rgba(16,24,40,.18); }
    .modal-header { border-bottom: ${T.border}; padding: 18px 22px; }
    .modal-title { font-weight: 700; color: ${Colors.TEXT_DARK}; font-size: 16px; }
    .modal-body { padding: 20px 22px; }
    .modal-footer { border-top: ${T.border}; padding: 14px 22px; }

    .page-item.active .page-link { background: ${Colors.PRIMARY} !important; border-color: ${Colors.PRIMARY} !important; color: #fff !important; }
    .page-link { color: ${Colors.TEXT_DARK} !important; border-color: ${Colors.BORDER_GRAY} !important; }
    .page-link:hover { background: ${Colors.LIGHT_GRAY} !important; }

    .dropdown-menu { border-radius: ${T.radiusSm}; border: ${T.border}; box-shadow: ${T.shadow}; padding: 6px; min-width: 220px; }
    .dropdown-item { border-radius: 6px; padding: 8px 12px; font-size: 13px; font-weight: 500; }
    .dropdown-item:hover { background: ${Colors.LIGHT_GRAY} !important; }
    .dropdown-item:active { background: ${Colors.PRIMARY}1a !important; color: ${Colors.TEXT_DARK} !important; }
    .dropdown-divider { margin: 4px 0; border-color: ${Colors.BORDER_GRAY}; }

    .nav-tabs { border-bottom: ${T.border}; gap: 4px; }
    .nav-tabs .nav-link { border: none !important; color: ${Colors.TEXT_LIGHT}; font-weight: 500; font-size: 13.5px; padding: 9px 15px; border-radius: ${T.radiusSm} ${T.radiusSm} 0 0; }
    .nav-tabs .nav-link:hover { color: ${Colors.TEXT_DARK}; background: ${Colors.LIGHT_GRAY}; }
    .nav-tabs .nav-link.active { color: ${Colors.PRIMARY} !important; background: #fff !important; border-bottom: 2px solid ${Colors.PRIMARY} !important; }

    .docs-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 12px; }
    .doc-card { border-radius: ${T.radiusSm}; border: 1px solid ${Colors.BORDER_GRAY}; background: #fff; overflow: hidden; transition: all .15s; }
    .doc-card.doc-present { border-color: ${Colors.SUCCESS}66; }
    .doc-card.doc-missing:not(.doc-optional) { border-color: ${Colors.RED}66; background: #fef2f2; }
    .doc-card.doc-optional { opacity: .92; }
    .doc-card-header { display: flex; align-items: center; justify-content: space-between; padding: 8px 10px; border-bottom: 1px solid ${Colors.BORDER_GRAY}; gap: 8px; }
    .doc-label { font-size: 11.5px; font-weight: 600; color: ${Colors.TEXT_DARK}; line-height: 1.3; flex: 1; }
    .doc-badge { font-size: 9.5px; font-weight: 700; padding: 2px 6px; border-radius: 4px; text-transform: uppercase; letter-spacing: .04em; white-space: nowrap; }
    .doc-badge-required { background: #fef3c7; color: #92400e; }
    .doc-badge-optional { background: #e5e7eb; color: #4b5563; }
    .doc-preview { position: relative; display: block; aspect-ratio: 4/3; overflow: hidden; background: ${Colors.LIGHT_GRAY}; }
    .doc-preview img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .doc-preview-overlay { position: absolute; inset: 0; background: rgba(0, 0, 0, .55); color: #fff; display: flex; align-items: center; justify-content: center; gap: 6px; font-size: 12px; font-weight: 600; opacity: 0; transition: opacity .15s; }
    .doc-preview:hover .doc-preview-overlay { opacity: 1; }
    .doc-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; padding: 20px 12px; color: ${Colors.GRAY}; aspect-ratio: 4/3; }
    .doc-empty-icon { font-size: 26px; }
    .doc-empty-text { font-size: 11px; font-weight: 500; }
  `}</style>
);

/* ================================================================== */
/* PRESENTATIONAL COMPONENTS                                          */
/* ================================================================== */

const StatusPill = ({ status }) => {
  const m = getStatusMeta(status);
  return (
    <span className="bk-pill" style={{ color: m.color, background: m.bg }}>
      {m.label}
    </span>
  );
};

const ColoredPill = ({ color, children }) => (
  <span className="bk-pill" style={{ color, background: `${color}1f` }}>
    {children}
  </span>
);

const DocumentGrid = ({ operator }) => {
  const docStatus = operator?.documents_status || {};

  const isPresent = (key) => {
    if (key === "government_id_image")
      return docStatus.government_id ?? !!operator.government_id_image;
    if (key === "cpc_or_provisional_authority")
      return (
        docStatus.cpc_or_provisional_authority ??
        !!operator.cpc_or_provisional_authority
      );
    if (key === "insurance_image")
      return docStatus.insurance ?? !!operator.insurance_image;
    if (key === "business_permit_image")
      return docStatus.business_permit ?? !!operator.business_permit_image;
    if (key === "confirmation_certificate")
      return (
        docStatus.confirmation_certificate ??
        !!operator.confirmation_certificate
      );
    return false;
  };

  return (
    <div className="docs-grid">
      {DOCUMENT_SLOTS.map(({ key, label, required }) => {
        const present = isPresent(key);
        const url = buildDocumentUrl(operator?.[key]);

        return (
          <div
            key={key}
            className={[
              "doc-card",
              present ? "doc-present" : "doc-missing",
              !required ? "doc-optional" : "",
            ].join(" ")}
          >
            <div className="doc-card-header">
              <span className="doc-label">{label}</span>
              {required ? (
                <span className="doc-badge doc-badge-required">Required</span>
              ) : (
                <span className="doc-badge doc-badge-optional">Optional</span>
              )}
            </div>

            {present && url ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="doc-preview"
                aria-label={`View ${label}`}
              >
                <img
                  src={url}
                  alt={label}
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
                <div className="doc-preview-overlay">
                  <i className="bi bi-box-arrow-up-right" />
                  View
                </div>
              </a>
            ) : (
              <div className="doc-empty">
                <i
                  className={`bi ${required ? "bi-exclamation-circle" : "bi-dash-circle"} doc-empty-icon`}
                />
                <span className="doc-empty-text">
                  {required ? "Not uploaded" : "Not provided"}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ---------------- Row actions (payout now navigates) ---------------- */
const RowActions = ({
  operator,
  statusLower,
  failedCount,
  isBlockedFromActivation,
  canApprove,
  updating,
  onView,
  onApprove,
  onReject,
  onDeactivate,
  onActivate,
  onRecordFailed,
  onMarkSettled,
  onPayout,
}) => {
  const isPending = statusLower === "pending";
  const isActive = statusLower === "active";
  const isInactiveOrRejected =
    statusLower === "inactive" || statusLower === "rejected";

  return (
    <div className="d-inline-flex gap-1 align-items-center">
      <Button
        className="bk-btn-ghost"
        size="sm"
        onClick={() => onView(operator.operator_id)}
        disabled={updating}
      >
        <i className="bi bi-eye me-1" />
        View
      </Button>

      {isPending && (
        <Button
          className="bk-btn-primary"
          size="sm"
          onClick={() => onApprove(operator.operator_id)}
          disabled={updating || !canApprove}
          title={
            !canApprove ? "Missing required documents" : "Approve operator"
          }
          style={{ background: Colors.SUCCESS }}
        >
          <i className="bi bi-check-circle me-1" />
          Approve
        </Button>
      )}

      {isActive && (
        <Button
          className="bk-btn-primary"
          size="sm"
          onClick={() => onPayout(operator)}
          disabled={updating}
        >
          <i className="bi bi-cash-stack me-1" />
          Payout
        </Button>
      )}

      {isInactiveOrRejected && (
        <Button
          className="bk-btn-primary"
          size="sm"
          onClick={() => onActivate(operator.operator_id)}
          disabled={updating || isBlockedFromActivation}
          title={
            isBlockedFromActivation
              ? "Settle commissions first"
              : "Activate operator"
          }
          style={{ background: Colors.SUCCESS }}
        >
          <i className="bi bi-play me-1" />
          Activate
        </Button>
      )}

      <Dropdown align="end">
        <Dropdown.Toggle as="button" className="bk-icon-btn">
          <i className="bi bi-three-dots" />
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {isPending && (
            <>
              <Dropdown.Item
                onClick={() => onApprove(operator.operator_id)}
                disabled={!canApprove}
              >
                <i
                  className="bi bi-check-circle me-2"
                  style={{ color: Colors.SUCCESS }}
                />
                Approve Operator
              </Dropdown.Item>
              <Dropdown.Item onClick={() => onReject(operator.operator_id)}>
                <i
                  className="bi bi-x-circle me-2"
                  style={{ color: Colors.RED }}
                />
                Reject Operator
              </Dropdown.Item>
              <Dropdown.Divider />
            </>
          )}

          {isActive && (
            <Dropdown.Item onClick={() => onDeactivate(operator.operator_id)}>
              <i
                className="bi bi-pause me-2"
                style={{ color: Colors.WARNING }}
              />
              Deactivate
            </Dropdown.Item>
          )}

          {isInactiveOrRejected && (
            <Dropdown.Item
              onClick={() => onActivate(operator.operator_id)}
              disabled={isBlockedFromActivation}
            >
              <i
                className="bi bi-play me-2"
                style={{ color: Colors.SUCCESS }}
              />
              Activate
            </Dropdown.Item>
          )}

          <Dropdown.Item onClick={() => onRecordFailed(operator.operator_id)}>
            <i
              className="bi bi-exclamation-triangle me-2"
              style={{ color: Colors.RED }}
            />
            Record Failed Commission
          </Dropdown.Item>

          {failedCount > 0 && (
            <Dropdown.Item onClick={() => onMarkSettled(operator.operator_id)}>
              <i
                className="bi bi-check2-circle me-2"
                style={{ color: Colors.INFO }}
              />
              Mark Commission as Settled
            </Dropdown.Item>
          )}

          <Dropdown.Divider />

          <Dropdown.Item onClick={() => onPayout(operator)}>
            <i
              className="bi bi-cash-stack me-2"
              style={{ color: Colors.PRIMARY }}
            />
            Send Payout
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

/* ================================================================== */
/* MAIN COMPONENT                                                     */
/* ================================================================== */

const Operators = () => {
  const navigate = useNavigate();
  const abortControllerRef = useRef(null);

  /* ---------------- List state ---------------- */
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [updating, setUpdating] = useState(false);

  /* ---------------- Detail modal ---------------- */
  const [showModal, setShowModal] = useState(false);
  const [detailOperator, setDetailOperator] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState("");
  const [mapData, setMapData] = useState(null);

  /* ---------------- Reject modal ---------------- */
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectingId, setRejectingId] = useState(null);

  /* ================================================================ */
  /* DATA FETCHING                                                    */
  /* ================================================================ */

  const fetchOperators = useCallback(
    async (signal) => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get("/admin/operators", {
          params: {
            page: currentPage,
            per_page: ITEMS_PER_PAGE,
            search: searchQuery || undefined,
            status: statusFilter !== "All" ? statusFilter : undefined,
          },
          signal,
        });
        setOperators(data.data || []);
        setTotalPages(data.last_page || 1);
        setTotalItems(data.total || 0);
      } catch (err) {
        if (err.name === "AbortError" || err.code === "ERR_CANCELED") return;
        if (err.response?.status === 401) {
          localStorage.removeItem("arkila_admin_token");
          navigate("/login");
        } else {
          showErrorAlert(
            "Error",
            err.response?.data?.message || "Failed to load operators.",
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [currentPage, searchQuery, statusFilter, navigate],
  );

  useEffect(() => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    fetchOperators(abortControllerRef.current.signal);
    return () => abortControllerRef.current?.abort();
  }, [fetchOperators]);

  const refreshList = () => {
    const c = new AbortController();
    abortControllerRef.current = c;
    fetchOperators(c.signal);
  };

  const fetchOperatorLocation = async (operatorId) => {
    setMapLoading(true);
    setMapError("");
    setMapData(null);
    try {
      let data;
      try {
        const res = await api.get(`/admin/operators/${operatorId}/location`);
        data = res.data;
      } catch {
        const res = await api.get(`/admin/operators/${operatorId}`);
        data = res.data;
      }
      const op = data.data || data;
      if (op.latitude && op.longitude) {
        setMapData({
          latitude: op.latitude,
          longitude: op.longitude,
          last_update: op.last_location_update || null,
          is_online: !!op.is_online,
          location: op.location || null,
          address: op.address || null,
        });
      } else {
        setMapError("No location data available for this operator");
      }
    } catch (err) {
      setMapError(
        "Failed to fetch location: " +
          (err.response?.data?.message || err.message),
      );
    } finally {
      setMapLoading(false);
    }
  };

  /* ================================================================ */
  /* ACTIONS                                                          */
  /* ================================================================ */

  const openModal = async (id) => {
    try {
      const { data } = await api.get(`/admin/operators/${id}`);
      const op = data.data || data;
      setDetailOperator(op);
      fetchOperatorLocation(id);
      setShowModal(true);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 404)
        showErrorAlert("Not Found", "Operator not found.");
      else if (err.response?.status === 401) {
        localStorage.removeItem("arkila_admin_token");
        navigate("/login");
      } else showErrorAlert("Error", "Failed to load operator details.");
    }
  };

  const handleCloseModal = () => {
    if (!updating) {
      setShowModal(false);
      setMapData(null);
      setMapError("");
      setDetailOperator(null);
    }
  };

  const handleStatusChange = async (operatorId, newStatus) => {
    const isDeactivating = newStatus.toLowerCase() === "inactive";
    const label = isDeactivating ? "deactivate" : "activate";
    const result = await showConfirmationAlert(
      "Confirm Status Change",
      `Are you sure you want to ${label} this operator?`,
      `Yes, ${label}!`,
    );
    if (!result.isConfirmed) return;
    try {
      showLoadingAlert("Updating...", "Please wait while we update the status");
      await api.patch(`/admin/operators/${operatorId}/status`, {
        status: newStatus.toLowerCase(),
      });
      SwalOnTop.close();
      const msg = `Operator ${isDeactivating ? "deactivated" : "activated"} successfully!`;
      setSuccess(msg);
      showSuccessAlert("Status Updated!", msg);
      refreshList();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      SwalOnTop.close();
      if (err.response?.status === 422)
        showErrorAlert(
          "Action Blocked",
          err.response?.data?.message || "Cannot activate this operator.",
        );
      else
        showErrorAlert(
          "Error",
          err.response?.data?.message || "Failed to change operator status.",
        );
    }
  };

  const handleApprove = async (operatorId) => {
    const result = await showConfirmationAlert(
      "Approve Operator?",
      "This will activate the operator account. Make sure all required documents are present.",
      "Yes, Approve",
    );
    if (!result.isConfirmed) return;
    try {
      showLoadingAlert("Approving...", "Please wait");
      await api.post(`/admin/operators/${operatorId}/approve`);
      SwalOnTop.close();
      showSuccessAlert(
        "Approved!",
        "Operator has been approved and activated.",
      );
      setSuccess("Operator approved successfully!");
      refreshList();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      SwalOnTop.close();
      const missing = err.response?.data?.missing_documents || [];
      const baseMsg =
        err.response?.data?.message || "Failed to approve operator.";
      MySwal.fire({
        icon: "error",
        title: "Cannot Approve",
        html: missing.length
          ? `${baseMsg}<br/><br/><strong>Missing:</strong> ${missing.join(", ")}`
          : baseMsg,
        confirmButtonColor: Colors.PRIMARY,
      });
    }
  };

  const openRejectModal = (operatorId) => {
    setRejectingId(operatorId);
    setRejectReason("");
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!rejectReason || rejectReason.trim().length < 10) {
      showErrorAlert(
        "Invalid Reason",
        "Please provide a rejection reason (at least 10 characters).",
      );
      return;
    }
    try {
      showLoadingAlert("Rejecting...", "Please wait");
      await api.post(`/admin/operators/${rejectingId}/reject`, {
        rejection_reason: rejectReason.trim(),
      });
      SwalOnTop.close();
      setShowRejectModal(false);
      showSuccessAlert("Rejected", "Operator has been rejected.");
      setSuccess("Operator rejected successfully!");
      refreshList();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      SwalOnTop.close();
      showErrorAlert(
        "Error",
        err.response?.data?.message || "Failed to reject operator.",
      );
    }
  };

  const handleRecordFailedCommission = async (operatorId) => {
    const result = await showConfirmationAlert(
      "Record Failed Commission?",
      "This will increase the failed commission count by 1. At 3, the operator is auto-deactivated.",
      "Yes, record it",
    );
    if (!result.isConfirmed) return;
    try {
      showLoadingAlert("Recording...", "Please wait");
      await api.post(`/admin/operators/${operatorId}/record-failed-commission`);
      SwalOnTop.close();
      setSuccess("Failed commission recorded successfully!");
      showSuccessAlert("Recorded!", "Failed commission has been recorded.");
      refreshList();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      SwalOnTop.close();
      showErrorAlert(
        "Error",
        err.response?.data?.message || "Failed to record commission failure.",
      );
    }
  };

  const handleMarkAsSettled = async (operatorId) => {
    const result = await showConfirmationAlert(
      "Mark as Settled?",
      "This will reset the failed commission count to 0.",
      "Yes, mark as settled",
    );
    if (!result.isConfirmed) return;
    try {
      showLoadingAlert("Updating...", "Please wait");
      await api.post(`/admin/operators/${operatorId}/mark-commission-settled`);
      SwalOnTop.close();
      setSuccess("Commission marked as settled!");
      showSuccessAlert("Settled!", "Failed commission count has been reset.");
      refreshList();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      SwalOnTop.close();
      showErrorAlert(
        "Error",
        err.response?.data?.message || "Failed to mark as settled.",
      );
    }
  };

  /* ---------------- Payout → navigate to Payouts page ---------------- */
  const handlePayout = (operator) => {
    navigate(`/payments/payouts?operator=${operator.operator_id}`);
  };

  /* ================================================================ */
  /* RENDER HELPERS                                                   */
  /* ================================================================ */

  const renderMapPreview = () => {
    if (mapLoading)
      return (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted mb-0">Loading location…</p>
        </div>
      );
    if (mapError)
      return (
        <div className="text-center py-5" style={{ color: Colors.GRAY }}>
          <div
            style={{
              width: 72,
              height: 72,
              margin: "0 auto 16px",
              borderRadius: "50%",
              background: Colors.LIGHT_GRAY,
              display: "grid",
              placeItems: "center",
              fontSize: 30,
            }}
          >
            <i className="bi bi-geo-alt" />
          </div>
          <p className="mb-0" style={{ color: Colors.TEXT_LIGHT }}>
            {mapError}
          </p>
        </div>
      );
    if (mapData) {
      const url = `https://maps.google.com/maps?q=${mapData.latitude},${mapData.longitude}&z=15&output=embed`;
      return (
        <div>
          <div className="bk-section">
            <div className="bk-section-title">
              <i className="bi bi-geo" /> Last Known Location
            </div>
            <div className="bk-info-row">
              <span className="bk-info-label">Status</span>
              <span className="bk-info-value">
                <StatusPill
                  status={mapData.is_online ? "Active" : "Inactive"}
                />
              </span>
            </div>
            <div className="bk-info-row">
              <span className="bk-info-label">Updated</span>
              <span className="bk-info-value">
                {formatDateTime(mapData.last_update)}
              </span>
            </div>
            {(mapData.address || mapData.location) && (
              <div className="bk-info-row">
                <span className="bk-info-label">Address</span>
                <span className="bk-info-value">
                  {mapData.address || mapData.location}
                </span>
              </div>
            )}
            <div className="bk-info-row">
              <span className="bk-info-label">Coordinates</span>
              <span
                className="bk-info-value"
                style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}
              >
                {mapData.latitude}, {mapData.longitude}
              </span>
            </div>
          </div>
          <div
            style={{
              borderRadius: T.radiusSm,
              overflow: "hidden",
              border: T.border,
              aspectRatio: "16 / 9",
            }}
          >
            <iframe
              src={url}
              style={{
                border: 0,
                width: "100%",
                height: "100%",
                display: "block",
              }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Operator Location"
            />
          </div>
        </div>
      );
    }
    return (
      <div className="text-center py-5" style={{ color: Colors.GRAY }}>
        <div
          style={{
            width: 72,
            height: 72,
            margin: "0 auto 16px",
            borderRadius: "50%",
            background: Colors.LIGHT_GRAY,
            display: "grid",
            placeItems: "center",
            fontSize: 30,
          }}
        >
          <i className="bi bi-geo-alt" />
        </div>
        <p className="mb-0" style={{ color: Colors.TEXT_LIGHT }}>
          No location data
        </p>
      </div>
    );
  };

  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const paginate = (p) => setCurrentPage(p);

  const renderPaginationItems = () => {
    const items = [];
    const max = 5;
    let start = Math.max(1, currentPage - Math.floor(max / 2));
    const end = Math.min(totalPages, start + max - 1);
    if (end - start + 1 < max) start = Math.max(1, end - max + 1);
    for (let n = start; n <= end; n++) {
      items.push(
        <Pagination.Item
          key={n}
          active={n === currentPage}
          onClick={() => paginate(n)}
        >
          {n}
        </Pagination.Item>,
      );
    }
    return items;
  };

  if (loading && operators.length === 0) {
    return (
      <div className="bk-page">
        <Styles />
        <Container fluid>
          <div className="bk-header">
            <h2>Operators Management</h2>
            <p>Loading operator accounts…</p>
          </div>
          <Row className="g-3 mb-4">
            {[1, 2, 3, 4].map((i) => (
              <Col key={i} xs={6} md={3}>
                <div className="bk-stat">
                  <div
                    className="bk-skel"
                    style={{ width: 44, height: 44, borderRadius: 11 }}
                  />
                  <div className="flex-grow-1">
                    <div
                      className="bk-skel mb-2"
                      style={{ height: 18, width: "50%" }}
                    />
                    <div
                      className="bk-skel"
                      style={{ height: 11, width: "70%" }}
                    />
                  </div>
                </div>
              </Col>
            ))}
          </Row>
          <div className="bk-card" style={{ padding: 20 }}>
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bk-skel mb-3" style={{ height: 42 }} />
            ))}
          </div>
        </Container>
      </div>
    );
  }

  const counts = {
    Active: operators.filter((o) => (o.status || "").toLowerCase() === "active")
      .length,
    Pending: operators.filter(
      (o) => (o.status || "").toLowerCase() === "pending",
    ).length,
    Inactive: operators.filter(
      (o) => (o.status || "").toLowerCase() === "inactive",
    ).length,
    Total: totalItems,
  };

  return (
    <div className="bk-page">
      <Styles />
      <Container fluid>
        <div className="bk-header">
          <div className="position-relative" style={{ zIndex: 1 }}>
            <h2>Operators Management</h2>
            <p>
              Review documents, approve/reject operators, and manage commission
              settlement
            </p>
          </div>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError("")}>
            <i className="bi bi-exclamation-triangle me-2" />
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess("")}>
            <i className="bi bi-check-circle me-2" />
            {success}
          </Alert>
        )}

        <Row className="g-3 mb-4">
          {[
            {
              k: "Active",
              v: counts.Active,
              i: "bi-person-check",
              c: Colors.SUCCESS,
            },
            {
              k: "Pending",
              v: counts.Pending,
              i: "bi-hourglass",
              c: Colors.WARNING,
            },
            {
              k: "Inactive",
              v: counts.Inactive,
              i: "bi-person-dash",
              c: Colors.RED,
            },
            { k: "Total", v: counts.Total, i: "bi-people", c: Colors.PRIMARY },
          ].map((s) => (
            <Col key={s.k} xs={6} md={3}>
              <div className="bk-stat">
                <div
                  className="bk-stat-icon"
                  style={{ background: `${s.c}1f`, color: s.c }}
                >
                  <i className={`bi ${s.i}`} />
                </div>
                <div>
                  <div className="bk-stat-val">{s.v}</div>
                  <div className="bk-stat-lbl">{s.k}</div>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        <div className="bk-toolbar">
          <Row className="g-2">
            <Col md={8}>
              <InputGroup>
                <InputGroup.Text className="bk-search-icon">
                  <i className="bi bi-search" />
                </InputGroup.Text>
                <FormControl
                  className="bk-search"
                  placeholder="Search by name, email, or phone…"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </InputGroup>
            </Col>
            <Col md={4}>
              <Dropdown>
                <Dropdown.Toggle className="bk-filter-btn w-100">
                  <i className="bi bi-funnel me-2" />
                  {STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label}
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100">
                  {STATUS_OPTIONS.map((o) => (
                    <Dropdown.Item
                      key={o.value}
                      active={statusFilter === o.value}
                      onClick={() => {
                        setStatusFilter(o.value);
                        setCurrentPage(1);
                      }}
                    >
                      {o.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          </Row>
        </div>

        <div className="bk-card">
          <div className="bk-card-header">
            <h5>
              <i
                className="bi bi-person-gear"
                style={{ color: Colors.PRIMARY }}
              />
              Operator Accounts
            </h5>
            <small>
              Showing {Math.min(indexOfFirstItem + 1, totalItems)}–
              {Math.min(indexOfLastItem, totalItems)} of {totalItems}
            </small>
          </div>

          {operators.length === 0 ? (
            <div className="bk-empty">
              <div className="bk-empty-icon">
                <i className="bi bi-person-gear" />
              </div>
              <h5 style={{ color: Colors.TEXT_DARK }}>No operators found</h5>
              <p className="text-muted mb-0">
                {searchQuery || statusFilter !== "All"
                  ? "Try adjusting your search or filter."
                  : "There are no operators in the system yet."}
              </p>
            </div>
          ) : (
            <>
              <Table responsive hover className="bk-table">
                <thead>
                  <tr>
                    <th>Operator</th>
                    <th>Contact</th>
                    <th>Balance</th>
                    <th>Status</th>
                    <th>Docs</th>
                    <th>Online</th>
                    <th className="col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {operators.map((op) => {
                    const statusLower = (op.status || "").toLowerCase();
                    const failedCount = op.failed_commission_count || 0;
                    const isBlockedFromActivation =
                      failedCount >= COMMISSION_FAIL_THRESHOLD;
                    const requiredComplete = hasCompleteRequiredDocuments(op);
                    const missingDocs = getMissingRequiredDocs(op);
                    const canApprove =
                      statusLower === "pending" && requiredComplete;

                    return (
                      <tr key={op.operator_id}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div className="bk-avatar">
                              {initials(op.first_name, op.last_name)}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div
                                style={{
                                  fontWeight: 600,
                                  color: Colors.TEXT_DARK,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  flexWrap: "wrap",
                                }}
                              >
                                {op.first_name} {op.last_name}
                                <span className="bk-id-chip">
                                  #{op.operator_id}
                                </span>
                              </div>
                              <small className="text-muted d-block">
                                {op.email}
                              </small>
                            </div>
                          </div>
                        </td>

                        <td>
                          <span style={{ color: Colors.TEXT_LIGHT }}>
                            {op.phone || "—"}
                          </span>
                        </td>

                        {/* Balance — wallet + cash commission owed */}
                        <td>
                          <div className="d-flex flex-column gap-1 align-items-start">
                            <span
                              style={{
                                fontFamily:
                                  'ui-monospace, "SF Mono", Menlo, monospace',
                                fontWeight: 700,
                                color:
                                  (op.wallet_balance || 0) > 0
                                    ? Colors.SUCCESS
                                    : Colors.TEXT_LIGHT,
                                fontSize: 13,
                              }}
                            >
                              ₱
                              {Number(op.wallet_balance || 0).toLocaleString(
                                "en-PH",
                                {
                                  minimumFractionDigits: 2,
                                },
                              )}
                            </span>
                            {Number(op.cash_commission_owed || 0) > 0 && (
                              <span
                                className="bk-pill"
                                style={{
                                  color: Colors.WARNING,
                                  background: `${Colors.WARNING}1f`,
                                  fontSize: 10,
                                  padding: "2px 7px",
                                }}
                                title="Cash commission owed to Arkila — will be collected on next payout"
                              >
                                owes ₱
                                {Number(op.cash_commission_owed).toLocaleString(
                                  "en-PH",
                                  {
                                    minimumFractionDigits: 2,
                                  },
                                )}
                              </span>
                            )}
                          </div>
                        </td>

                        <td>
                          <div className="d-flex flex-column gap-1 align-items-start">
                            <StatusPill status={op.status} />
                            {failedCount > 0 && (
                              <span
                                className="bk-pill"
                                style={{
                                  color:
                                    failedCount >= COMMISSION_FAIL_THRESHOLD
                                      ? Colors.RED
                                      : Colors.WARNING,
                                  background:
                                    failedCount >= COMMISSION_FAIL_THRESHOLD
                                      ? `${Colors.RED}1f`
                                      : `${Colors.WARNING}1f`,
                                  fontSize: 10.5,
                                  padding: "2px 8px",
                                }}
                              >
                                {failedCount} failed commission
                                {failedCount > 1 ? "s" : ""}
                              </span>
                            )}
                          </div>
                        </td>

                        <td>
                          {requiredComplete ? (
                            <ColoredPill color={Colors.SUCCESS}>
                              Complete
                            </ColoredPill>
                          ) : (
                            <span
                              className="bk-pill"
                              style={{
                                color: Colors.WARNING,
                                background: `${Colors.WARNING}1f`,
                              }}
                              title={`Missing: ${missingDocs.join(", ")}`}
                            >
                              <i className="bi bi-exclamation-triangle me-1" />
                              Incomplete
                            </span>
                          )}
                        </td>

                        <td>
                          {op.is_online ? (
                            <ColoredPill color={Colors.SUCCESS}>
                              Online
                            </ColoredPill>
                          ) : (
                            <ColoredPill color={Colors.GRAY}>
                              Offline
                            </ColoredPill>
                          )}
                        </td>

                        <td className="col-actions">
                          <RowActions
                            operator={op}
                            statusLower={statusLower}
                            failedCount={failedCount}
                            isBlockedFromActivation={isBlockedFromActivation}
                            canApprove={canApprove}
                            updating={updating}
                            onView={openModal}
                            onApprove={handleApprove}
                            onReject={openRejectModal}
                            onDeactivate={(id) =>
                              handleStatusChange(id, "inactive")
                            }
                            onActivate={(id) =>
                              handleStatusChange(id, "active")
                            }
                            onRecordFailed={handleRecordFailedCommission}
                            onMarkSettled={handleMarkAsSettled}
                            onPayout={handlePayout}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              {totalPages > 1 && (
                <div
                  className="d-flex justify-content-between align-items-center flex-wrap gap-2"
                  style={{ padding: "14px 20px", borderTop: T.border }}
                >
                  <small className="text-muted">
                    Page {currentPage} of {totalPages}
                  </small>
                  <Pagination className="mb-0">
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => paginate(currentPage - 1)}
                    />
                    {renderPaginationItems()}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => paginate(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </div>

        {/* ============ DETAIL MODAL ============ */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
          <Modal.Header closeButton={!updating}>
            <Modal.Title>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    background: `${Colors.PRIMARY}1f`,
                    color: Colors.PRIMARY,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <i className="bi bi-person-gear" />
                </span>
                Operator Details
                {detailOperator &&
                  (hasCompleteRequiredDocuments(detailOperator) ? (
                    <span
                      className="bk-pill"
                      style={{
                        color: Colors.SUCCESS,
                        background: `${Colors.SUCCESS}1f`,
                        marginLeft: 8,
                      }}
                    >
                      Docs Complete
                    </span>
                  ) : (
                    <span
                      className="bk-pill"
                      style={{
                        color: Colors.WARNING,
                        background: `${Colors.WARNING}1f`,
                        marginLeft: 8,
                      }}
                    >
                      Docs Incomplete
                    </span>
                  ))}
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {!detailOperator ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted mb-0">Loading operator…</p>
              </div>
            ) : (
              <Tabs defaultActiveKey="details" className="mb-3">
                <Tab eventKey="details" title="Details">
                  <div
                    style={{
                      maxHeight: "60vh",
                      overflowY: "auto",
                      paddingRight: "0.25rem",
                    }}
                  >
                    <div className="bk-section">
                      <div className="bk-section-title">
                        <i className="bi bi-person" /> Identity
                      </div>
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="bk-avatar"
                          style={{
                            width: 48,
                            height: 48,
                            fontSize: 16,
                            borderRadius: 14,
                          }}
                        >
                          {initials(
                            detailOperator.first_name,
                            detailOperator.last_name,
                          )}
                        </div>
                        <div>
                          <div
                            style={{ fontWeight: 700, color: Colors.TEXT_DARK }}
                          >
                            {detailOperator.first_name}{" "}
                            {detailOperator.last_name}
                          </div>
                          <small className="text-muted">
                            {detailOperator.email}
                          </small>
                        </div>
                      </div>
                    </div>

                    <div className="bk-section">
                      <div className="bk-section-title">
                        <i className="bi bi-envelope" /> Contact
                      </div>
                      <div className="bk-info-row">
                        <span className="bk-info-label">Email</span>
                        <span className="bk-info-value">
                          {detailOperator.email || "—"}
                        </span>
                      </div>
                      <div className="bk-info-row">
                        <span className="bk-info-label">Phone</span>
                        <span className="bk-info-value">
                          {detailOperator.phone || "—"}
                        </span>
                      </div>
                      <div className="bk-info-row">
                        <span className="bk-info-label">Address</span>
                        <span className="bk-info-value">
                          {detailOperator.address || "—"}
                        </span>
                      </div>
                      <div className="bk-info-row">
                        <span className="bk-info-label">Location</span>
                        <span className="bk-info-value">
                          {detailOperator.location || "—"}
                        </span>
                      </div>
                    </div>

                    <div className="bk-section">
                      <div className="bk-section-title">
                        <i className="bi bi-shield-check" /> Account
                      </div>
                      <div className="bk-info-row">
                        <span className="bk-info-label">Status</span>
                        <StatusPill status={detailOperator.status} />
                      </div>
                      <div className="bk-info-row">
                        <span className="bk-info-label">Online</span>
                        <StatusPill
                          status={
                            detailOperator.is_online ? "Active" : "Inactive"
                          }
                        />
                      </div>
                      <div className="bk-info-row">
                        <span className="bk-info-label">
                          Failed Commissions
                        </span>
                        <span className="bk-info-value">
                          {detailOperator.failed_commission_count || 0}
                          {detailOperator.failed_commission_count >=
                            COMMISSION_FAIL_THRESHOLD && (
                            <span
                              className="bk-pill ms-2"
                              style={{
                                color: Colors.RED,
                                background: `${Colors.RED}1f`,
                              }}
                            >
                              Auto-deactivated
                            </span>
                          )}
                        </span>
                      </div>
                      {detailOperator.rejection_reason && (
                        <div className="bk-info-row">
                          <span className="bk-info-label">
                            Rejection Reason
                          </span>
                          <span
                            className="bk-info-value"
                            style={{ color: Colors.RED }}
                          >
                            {detailOperator.rejection_reason}
                          </span>
                        </div>
                      )}
                      <div className="bk-info-row">
                        <span className="bk-info-label">Firebase UID</span>
                        <span
                          className="bk-info-value"
                          style={{
                            fontFamily: "ui-monospace, monospace",
                            fontSize: 11,
                          }}
                        >
                          {detailOperator.firebase_uid || "—"}
                        </span>
                      </div>
                      {detailOperator.last_location_update && (
                        <div className="bk-info-row">
                          <span className="bk-info-label">Last Update</span>
                          <span className="bk-info-value">
                            {formatDateTime(
                              detailOperator.last_location_update,
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </Tab>

                <Tab
                  eventKey="documents"
                  title={
                    <span>
                      Documents{" "}
                      {!hasCompleteRequiredDocuments(detailOperator) && (
                        <span
                          className="badge bg-warning text-dark"
                          style={{ fontSize: 10, marginLeft: 4 }}
                          title="Missing required documents"
                        >
                          !
                        </span>
                      )}
                    </span>
                  }
                >
                  <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
                    <DocumentGrid operator={detailOperator} />
                  </div>
                </Tab>

                <Tab eventKey="location" title="Location">
                  {renderMapPreview()}
                </Tab>
              </Tabs>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button className="bk-btn-ghost" onClick={handleCloseModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ============ REJECT MODAL ============ */}
        <Modal
          show={showRejectModal}
          onHide={() => setShowRejectModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    background: `${Colors.RED}1f`,
                    color: Colors.RED,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <i className="bi bi-x-circle" />
                </span>
                Reject Operator
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted" style={{ fontSize: 13.5 }}>
              Please provide a clear reason for rejection. This will be emailed
              to the operator.
            </p>
            <Form.Group>
              <Form.Label style={{ fontSize: 13, fontWeight: 500 }}>
                Rejection Reason <span style={{ color: Colors.RED }}>*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Example: Government ID is blurry…"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                style={{ borderRadius: T.radiusSm }}
              />
              <Form.Text className="text-muted">
                Minimum 10 characters required.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="bk-btn-ghost"
              onClick={() => setShowRejectModal(false)}
            >
              Cancel
            </Button>
            <Button
              className="bk-btn-primary"
              onClick={handleReject}
              disabled={!rejectReason || rejectReason.trim().length < 10}
              style={{ background: Colors.RED }}
            >
              Reject Operator
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default Operators;
